
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { AutocompleteFieldComponent } from '../../../../components/form/autocomplete-field/autocomplete-field.component';
import { AlertInfo } from '../../../../models/alert-info';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { ProposalsService } from '../../../../services/api/proposals.service';
import { FormService } from '../../../../services/form.service';
import { ValidatorService } from '../../../../services/validator.service';
import { RouterLink } from '@angular/router';
import { LanguageClientComponent } from '../../../../components/form/language-client/language-client.component';
import { CustomerService } from 'src/app/services/api/customer.service';

@Component({
  selector: 'proposals-form',
  imports: [RouterLink, InputFieldComponent, ReactiveFormsModule, NgbAlert, LanguageClientComponent, AutocompleteFieldComponent],
  templateUrl: './proposals-form.component.html',
  styleUrl: './proposals-form.component.scss'
})
export class ProposalsFormComponent {

  @Input() proposal?: any;
  @Input() readonlyProposalsForm: boolean = false;
  @Input() allowClientEdit: boolean = false;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  form!: FormGroup;
  selectedClientKey: number | null = null;

  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  constructor(
    private validatorService: ValidatorService,
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private proposalsService: ProposalsService,
    protected customerService: CustomerService
  ) { }


  ngOnInit(): void {
    this.form = new FormGroup({
      client: new FormControl('', [Validators.required]),
      companyCode: new FormControl('', [Validators.required]),
      brandName: new FormControl('',),
      language: new FormControl('eng', [Validators.required]),
    });

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['proposal'] && changes['proposal'].currentValue) {
        this.form.patchValue({
          client: this.proposal.customer.companyName,
          companyCode: this.proposal.request.customer.code,
          brandName: this.proposal.request.customer.brand,
          language: this.proposal.request.customer.language.iso3,
        }, { emitEvent: false });

        this.selectedClientKey = this.getCurrentCustomerId();
    }
  }


  /**
     * *******************
     * Submit form methods
     * *******************
     */

  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }
  submitFormSuccess(response: any) {
    this.onSavedData.emit({
      success: true,
      response
    });
  }
  submitFormError(error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });

  }
  submitForm(): void {

    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    if (this.form.valid) {

      const values = Object.assign(
        this.form.value,
        {
          customerId: this.normalizeCustomerId(this.getCurrentCustomerId()),
          tenantId: 1,
          isContractVerified: false,
          requestId: this.proposal?.request.requestId
        },
      );

      delete values.client;

      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);
      this.submitFormSuccess(values);
    }
  }
  /**
   * *******************
   * Submit form methods
   * *******************
   */

  getProposalClientInput(): FormControl { return this.getFormControl('client'); }
  getProposalCompanyCodeInput(): FormControl { return this.getFormControl('companyCode'); }
  getProposalBrandNameInput(): FormControl { return this.getFormControl('brandName'); }
  getProposalLanguageInput(): FormControl { return this.getFormControl('language'); }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }

  isClientReadonly(): boolean {
    return this.readonlyProposalsForm && !this.allowClientEdit;
  }

  onClientSearchValueChange(value: any): void {
    this.selectedClientKey = value ?? null;
  }

  private getCurrentCustomerId(): number | null {
    return this.selectedClientKey
      ?? this.proposal?.request?.customer?.customerId
      ?? this.proposal?.customer?.customerId
      ?? this.proposal?.customerId
      ?? null;
  }

  private normalizeCustomerId(value: number | string | null): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }
}
